/**
 * Three engagement models. Verbatim from design/Homepage.dc.html lines 130-217.
 *
 * Note on the stat values: "96" and "1–2" read like metrics but are service
 * descriptions, not client outcomes, and both are client-approved design copy.
 * Only "2.5×" is one of the two real metrics. Nothing here may be added to.
 */

export const modelsSection = {
  eyebrow: "Three engagement models",
  heading: "Pick the model. We bring the engineering.",
} as const;

export type EngagementModel = {
  number: string;
  /** Segmented-switcher label. Differs from `title` — see CLAUDE.md. */
  tab: string;
  badge: string;
  title: string;
  body: string;
  stat: { value: string; caption: string };
  /**
   * "problem" renders the stat in white with a blue rule instead of the yellow
   * numeral. Only model 01 uses it, because its figure is a failure rate rather
   * than a capability — see the note there. Defaults to the yellow treatment.
   */
  statTone?: "positive" | "problem";
  cta: { label: string; href: string };
  details: string[];
};

export const models: EngagementModel[] = [
  /**
   * DELIBERATE DEVIATION FROM design/Homepage.dc.html, at the client's request.
   *
   * This panel used to describe testing AI products somebody else built —
   * "Deployed agents behave differently every run", 96 adversarial cases,
   * hallucination benchmarking, prompt-injection suites. The service it links
   * to is built from WeAreQA-AI-Driven.pdf, which is about redesigning your
   * delivery operating model using AI. The panel and its own service page
   * described two different products.
   *
   * Everything below except `number`, `tab`, `title` and `cta` now comes from
   * that deck, so the homepage promises what the service page delivers. The
   * reference's original wording is in git and in Homepage.dc.html; do not
   * restore it.
   *
   * THE STAT IS A PROBLEM, NOT A CAPABILITY. "96 adversarial cases" was a thing
   * we do, so the yellow numeral suited it. A failure rate in that treatment
   * reads as a boast about failure, which is why `statTone` exists.
   *
   * **NO SOURCE FOR THE 70%+ FIGURE.** The deck asserts it uncited. It is an
   * industry claim, not a client outcome, so CLAUDE.md's closed metric list
   * does not cover it — but it now sits on the most-visited page on the site.
   * Get the source or drop it before launch.
   */
  {
    number: "01",
    tab: "AI-Driven Validation",
    /** Deck cover subtitle. */
    badge: "AI-Native Quality Engineering",
    title: "AI-Driven Validation",
    /** Deck slide 23. */
    body: "We help organizations transform software delivery through intelligent quality engineering.",
    /** Deck slide 5. */
    stat: {
      value: "70%+",
      caption: "of AI initiatives fail to deliver sustainable impact",
    },
    statTone: "problem",
    cta: { label: "Book an AI validation assessment", href: "#book" },
    /** Deck slide 6 — the five pillars of the AI-Native Delivery Framework. */
    details: [
      "People — Empowered teams with AI skills and mindset",
      "Processes — AI-native processes designed for speed, quality and adaptability",
      "Platform — Unified platform enabling visibility, integration and scale",
      "AI Capabilities — Embedded intelligence across the delivery value chain",
      "Outcomes — Measurable business impact and continuous improvement",
    ],
  },
  {
    number: "02",
    tab: "QA Consulting",
    badge: "Modernization",
    title: "QA Consulting & Modernization",
    body: "Manual bottlenecks slow releases. We audit the landscape, then rebuild the pipeline around quality gates.",
    stat: {
      value: "2.5×",
      caption: "lower dev-environment cost after our audit",
    },
    cta: { label: "Request a QA process audit", href: "#book" },
    details: [
      "Technical QA audit & architecture roadmap",
      "CI/CD integration and shift-left strategy",
      "Framework refactoring and performance benchmarks",
      "Release-management quality gates",
    ],
  },
  {
    number: "03",
    tab: "Quality Operations",
    badge: "Teams",
    title: "Intelligent Quality Operations",
    body: "A managed pod that owns delivery, or senior engineers embedded in your team on your tools.",
    stat: { value: "1–2", caption: "weeks to onboard, not months" },
    cta: { label: "Build your QA team", href: "#book" },
    details: [
      "Managed outsourcing — we own the outcome",
      "Outstaffing — pre-vetted engineers in your sprints",
      "Domain specialists matched to your regulations",
      "Zero recruitment overhead",
    ],
  },
];
